<script setup lang="ts">
  import CalendarIcon from '@/icons/calendar-icon.vue'
  import { FormFieldContainer, type Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { computed, useTemplateRef } from 'vue'

  defineProps<{
    label: string
    placeholder?: string | undefined
    error?: string | undefined
    required?: boolean | undefined
    disabled?: boolean | undefined
  }>()

  const emit = defineEmits<{
    (e: 'blur', event: FocusEvent): void
  }>()

  const modelValue = defineModel<Date | Nullish>()

  function formatForInput(date: Date) {
    function pad(value: number, count: number) {
      return value.toString().padStart(count, '0')
    }
    return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}T${pad(date.getHours(), 2)}:${pad(date.getMinutes(), 2)}`
  }
  const inputRef = useTemplateRef('datetimeRef')

  const internalModel = computed({
    get() {
      if (modelValue.value == null || isNaN(modelValue.value?.getDate())) return ''
      return formatForInput(modelValue.value)
    },
    set(value) {
      if (inputRef.value?.checkValidity()) {
        modelValue.value = value === '' ? null : new Date(value)
      } else {
        modelValue.value = new Date(NaN)
      }
    },
  })
</script>

<template>
  <FormFieldContainer :label="label" :optional="!required" :error="error">
    <div class="input-wrapper">
      <input
        ref="datetimeRef"
        v-model="internalModel"
        type="datetime-local"
        :disabled="disabled"
        :class="{ error: error }"
        :placeholder="placeholder"
        @blur="
          event => {
            internalModel = internalModel
            emit('blur', event)
          }
        "
      />
      <CalendarIcon class="input-icon" />
    </div>
  </FormFieldContainer>
</template>

<style scoped>
  .input-wrapper input {
    height: var(--components-component-height);
    width: 100%;
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours-grey-3);
    padding: var(--spacing-xs);
  }

  .input-wrapper input::-webkit-calendar-picker-indicator {
    opacity: 0.1%;
  }

  .input-wrapper input:hover {
    background: var(--colours-support-hover);
  }

  .input-wrapper input:focus {
    background: var(--colours---greys-grey-1);
    border-radius: var(--components-form-field-corners);
    border: 2px solid var(--colours-support-keyboard-focus);
  }

  .input-wrapper input:disabled {
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours---greys-grey-3);
    background: var(--colours---greys-grey-2);
  }

  .input-wrapper input.error {
    border-radius: var(--components-form-field-corners);
    border: 2px solid var(--colours-support-error);
  }

  .input-wrapper {
    position: relative;
    width: 100%;
    cursor: pointer;
  }

  .input-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .input-icon.disabled {
    color: var(--);
  }
</style>
